import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, MapPin, Smartphone, Tablet } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  TimeclockDevice,
  TimeclockMember,
  TimeclockMode,
  TimeclockTodayResponse,
} from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  ConfirmDialog,
  CopyButton,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Page,
  PageHeader,
  QueryState,
  Section,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  notify,
} from '@aeris/ui'

import { PixQr } from '../components/pix-qr.tsx'
import { PontoEscala } from '../components/ponto-escala.tsx'
import { PontoEspelho } from '../components/ponto-espelho.tsx'
import {
  ApiError,
  createTimeclockDevice,
  createTimeclockMember,
  fetchBranches,
  fetchTimeclockBranchSetting,
  fetchTimeclockDevices,
  fetchTimeclockMembers,
  fetchTimeclockToday,
  revokeTimeclockDevice,
  saveTimeclockBranchSetting,
  saveTimeclockMember,
  unlockTimeclockMember,
  uploadTimeclockMemberPhoto,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { isDesktop } from '../lib/desktop.ts'
import { PUNCH_LABELS } from '../lib/ponto-device.ts'

/**
 * O ponto dos funcionários (F339, ADR-0050), atrás da chave `ponto`.
 *
 * Controle interno do gerente: quem está na loja agora e as batidas do dia
 * com a foto, o código e a foto de referência de cada funcionário, e os
 * aparelhos que batem — o tablet da loja ou o celular de alguém. O gerente
 * escolhe, por pessoa, onde ela bate.
 */

const MODE_LABELS: Record<TimeclockMode, string> = {
  loja: 'Na loja',
  celular: 'No celular',
}

const hhmm = (iso: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))

const when = (iso: string | null): string =>
  iso === null
    ? 'nunca'
    : new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(iso))

const messageOf = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

/** Alguém mudou antes: a versão na tela é velha e mandar de novo não adianta. */
const isStale = (cause: unknown): boolean =>
  cause instanceof ApiError && cause.code === 'stale'

/**
 * O link vem do servidor. Sem endereço público, o do navegador serve — menos
 * no app desktop, que roda no proxy local e daria um link que o tablet não abre.
 */
const pairingLink = (created: {
  readonly token: string
  readonly link: string | null
}): string | null =>
  created.link ??
  (isDesktop()
    ? null
    : `${window.location.origin}/bater-ponto#${created.token}`)

const randomPin = (): string => {
  const bytes = new Uint32Array(1)
  crypto.getRandomValues(bytes)
  return String((bytes[0] ?? 0) % 10_000).padStart(4, '0')
}

// ---------------------------------------------------------------------------
// Hoje
// ---------------------------------------------------------------------------

type Person = TimeclockTodayResponse['people'][number]

const FlagBadge = ({ flag }: { flag: string }): ReactNode =>
  flag === 'fora_da_loja' ? (
    <Badge tone="warning">Fora da loja</Badge>
  ) : flag === 'sem_localizacao' ? (
    <Badge tone="warning">Sem localização</Badge>
  ) : null

const PersonDialog = ({
  person,
  onClose,
}: {
  person: Person
  onClose: () => void
}): ReactNode => (
  <Dialog
    open
    onOpenChange={(open) => {
      if (!open) onClose()
    }}
  >
    <DialogContent
      title={person.name}
      description={`As batidas de hoje — ${person.branchName}`}
    >
      <div className="flex flex-col gap-4">
        {person.referencePhotoUrl === null ? null : (
          <figure className="flex items-center gap-3">
            <img
              src={person.referencePhotoUrl}
              alt={`Foto de referência de ${person.name}`}
              className="h-20 w-20 rounded-(--radius-md) object-cover"
            />
            <figcaption className="text-(length:--text-sm) text-(--text-secondary)">
              Foto de referência, para comparar com as batidas.
            </figcaption>
          </figure>
        )}
        <ul className="grid gap-3 sm:grid-cols-2">
          {person.marks.map((mark) => (
            <li
              key={mark.id}
              className="flex gap-3 rounded-(--radius-md) border border-(--border-default) p-2"
            >
              {mark.photoUrl === null ? (
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--surface-sunken) text-center text-(length:--text-xs) text-(--text-secondary)">
                  Incluída pelo gerente
                </span>
              ) : (
                <a
                  href={mark.photoUrl.replace('/thumb', '/full')}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={mark.photoUrl}
                    alt={`Foto da batida de ${person.name} às ${hhmm(mark.at)}`}
                    className="h-20 w-20 rounded-(--radius-sm) object-cover"
                  />
                </a>
              )}
              <div className="flex flex-col gap-1 text-(length:--text-sm)">
                <span
                  className={`font-(--weight-medium) ${mark.voided === null ? '' : 'line-through opacity-60'}`}
                >
                  {PUNCH_LABELS[mark.kind]} às {hhmm(mark.at)}
                </span>
                {mark.deviceName === null ? null : (
                  <span className="text-(--text-secondary)">
                    {mark.deviceName} · nº {mark.seq}
                  </span>
                )}
                {mark.reason === null ? null : (
                  <span className="text-(--text-secondary)">
                    {mark.by}: {mark.reason}
                  </span>
                )}
                {mark.voided === null ? null : (
                  <span className="text-(--text-secondary)">
                    Anulada por {mark.voided.by}: {mark.voided.reason}
                  </span>
                )}
                {mark.distanceM === null ? null : (
                  <span className="text-(--text-secondary)">
                    A {mark.distanceM} m da loja
                  </span>
                )}
                {mark.flag === null ? null : <FlagBadge flag={mark.flag} />}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </DialogContent>
  </Dialog>
)

const TodayTab = (): ReactNode => {
  // Guarda quem, não a cópia: o diálogo acompanha a recarga de 30 s.
  const [openId, setOpenId] = useState<string | null>(null)
  const today = useQuery({
    queryKey: ['timeclock', 'today'],
    queryFn: async ({ signal }) => await fetchTimeclockToday(null, signal),
    refetchInterval: 30_000,
  })
  const open = today.data?.people.find((person) => person.employeeId === openId)

  return (
    <QueryState
      isPending={today.isPending}
      isError={today.isError}
      error={today.error}
      onRetry={() => {
        void today.refetch()
      }}
      what="o ponto de hoje"
      isEmpty={today.data?.people.length === 0}
      empty={
        <EmptyState
          icon={<Clock aria-hidden="true" className="h-8 w-8" />}
          title="Ninguém bate o ponto ainda"
          description="Cadastre o código de cada pessoa em Funcionários e pareie um aparelho em Aparelhos."
        />
      }
    >
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(today.data?.people ?? []).map((person) => {
          const valid = person.marks.filter((m) => m.voided === null)
          const last = valid.at(-1) ?? null
          const flagged = valid.find((m) => m.flag !== null && m.flag !== 'ok')
          return (
            <li key={person.employeeId}>
              <button
                type="button"
                disabled={person.marks.length === 0}
                onClick={() => {
                  setOpenId(person.employeeId)
                }}
                className="flex w-full items-center gap-3 rounded-(--radius-lg) border border-(--border-default) bg-(--surface) p-3 text-left enabled:hover:bg-(--surface-sunken)"
              >
                {(last?.photoUrl ?? person.referencePhotoUrl) === null ? (
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-(--surface-sunken) text-(--text-secondary)">
                    <Clock aria-hidden="true" className="h-6 w-6" />
                  </span>
                ) : (
                  <img
                    src={last?.photoUrl ?? person.referencePhotoUrl ?? ''}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-full object-cover"
                  />
                )}
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="truncate font-(--weight-medium)">
                    {person.name}
                  </span>
                  <span className="text-(length:--text-sm) text-(--text-secondary)">
                    {last === null
                      ? 'Não bateu hoje'
                      : person.inside
                        ? `Na loja desde ${hhmm(valid.find((m) => m.kind === 'entrada')?.at ?? last.at)}`
                        : `${PUNCH_LABELS[last.kind]} às ${hhmm(last.at)}`}
                    {person.shift === null
                      ? ''
                      : ` · ${person.shift.kind === 'folga' ? person.shift.name : `${person.shift.startsAt ?? ''}–${person.shift.endsAt ?? ''}`}`}
                  </span>
                  <span className="flex flex-wrap gap-1">
                    {person.inside ? (
                      <Badge tone="success">Na loja</Badge>
                    ) : null}
                    {person.late ? <Badge tone="danger">Atrasado</Badge> : null}
                    {flagged?.flag == null ? null : (
                      <FlagBadge flag={flagged.flag} />
                    )}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      {open === undefined ? null : (
        <PersonDialog
          person={open}
          onClose={() => {
            setOpenId(null)
          }}
        />
      )}
    </QueryState>
  )
}

// ---------------------------------------------------------------------------
// Funcionários
// ---------------------------------------------------------------------------

const MemberDialog = ({
  editing,
  onClose,
}: {
  /** `null` cria um funcionário novo, na folha também. */
  editing: TimeclockMember | null
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
    enabled: editing === null,
  })
  const current = editing?.member ?? null
  const [name, setName] = useState('')
  const [branchId, setBranchId] = useState('')
  const [pin, setPin] = useState(current === null ? randomPin() : '')
  const [modes, setModes] = useState<TimeclockMode[]>(
    current === null ? ['loja'] : [...current.allowedModes]
  )
  const [consent, setConsent] = useState(current?.consentAt != null)
  const [photo, setPhoto] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () => {
      const saved =
        editing === null
          ? await createTimeclockMember({
              name,
              branchId,
              pin,
              allowedModes: modes,
              consent,
            })
          : await saveTimeclockMember(editing.employeeId, {
              ...(pin === '' ? {} : { pin }),
              allowedModes: modes,
              consent,
              version: current?.version ?? null,
            })
      // O cadastro já está salvo: a foto que falha vira aviso, e não um erro
      // que faria o gerente salvar de novo (com a versão velha, ou criando
      // a pessoa duas vezes).
      let photoFailed: string | null = null
      if (photo !== null) {
        try {
          await uploadTimeclockMemberPhoto(saved.member.employeeId, photo)
        } catch (cause) {
          photoFailed = messageOf(cause, 'A foto não foi enviada.')
        }
      }
      return { saved, photoFailed }
    },
    onSuccess: async ({ photoFailed }) => {
      notify.success(
        pin === ''
          ? 'Cadastro salvo.'
          : `Cadastro salvo. Passe o código ${pin} para a pessoa.`
      )
      if (photoFailed !== null) {
        notify.error(
          `${photoFailed} Abra o cadastro de novo para mandar outra foto.`
        )
      }
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
      onClose()
    },
    onError: async (cause: unknown) => {
      if (isStale(cause)) {
        notify.error(messageOf(cause, 'Alguém mudou este cadastro.'))
        await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
        onClose()
        return
      }
      setError(messageOf(cause, 'Não foi possível salvar o cadastro.'))
    },
  })

  const toggle = (mode: TimeclockMode, on: boolean) => {
    setModes((list) =>
      on ? [...new Set([...list, mode])] : list.filter((m) => m !== mode)
    )
  }

  const pinOk = pin === '' ? current !== null : /^\d{4,6}$/u.test(pin)
  const ready =
    pinOk &&
    modes.length > 0 &&
    (editing !== null || (name.trim() !== '' && branchId !== ''))

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={editing === null ? 'Novo funcionário no ponto' : editing.name}
        description={
          editing === null
            ? 'A pessoa entra também na folha, sem salário — o salário continua sendo da folha.'
            : 'O código e onde a pessoa bate o ponto.'
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
          {editing === null ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nome" required>
                {(field) => (
                  <Input
                    {...field}
                    value={name}
                    maxLength={120}
                    onChange={(event) => {
                      setName(event.target.value)
                    }}
                  />
                )}
              </Field>
              <Field label="Loja" required>
                {(field) => (
                  <Select
                    {...field}
                    value={branchId}
                    disabled={branches.isPending}
                    onChange={(event) => {
                      setBranchId(event.target.value)
                    }}
                  >
                    <option value="">Escolha a loja</option>
                    {(branches.data?.branches ?? [])
                      .filter((branch) => branch.status === 'active')
                      .map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            </div>
          ) : null}

          <Field
            label="Código"
            required={current === null}
            hint={
              current === null
                ? 'De 4 a 6 números. Passe para a pessoa: ele não aparece de novo.'
                : 'Deixe em branco para manter o código. Um código novo destrava a pessoa.'
            }
          >
            {(field) => (
              <div className="flex gap-2">
                <Input
                  {...field}
                  value={pin}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={6}
                  onChange={(event) => {
                    setPin(event.target.value.replace(/\D/gu, ''))
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setPin(randomPin())
                  }}
                >
                  Gerar
                </Button>
              </div>
            )}
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-(length:--text-sm) font-(--weight-medium)">
              Onde bate o ponto
            </legend>
            <CheckboxField
              id="ponto-modo-loja"
              label="Na loja (tablet ou computador da loja)"
              checked={modes.includes('loja')}
              onCheckedChange={(checked) => {
                toggle('loja', checked === true)
              }}
            />
            <CheckboxField
              id="ponto-modo-celular"
              label="No celular dele, com a localização"
              checked={modes.includes('celular')}
              onCheckedChange={(checked) => {
                toggle('celular', checked === true)
              }}
            />
          </fieldset>

          <Field
            label="Foto de referência"
            hint="O rosto da pessoa, para comparar com a foto de cada batida."
          >
            {(field) => (
              <Input
                {...field}
                type="file"
                accept="image/*"
                capture="user"
                onChange={(event) => {
                  setPhoto(event.target.files?.[0] ?? null)
                }}
              />
            )}
          </Field>

          <CheckboxField
            id="ponto-aviso"
            label="A pessoa foi avisada de que a batida tira foto e, no celular, pega a localização."
            checked={consent}
            onCheckedChange={(checked) => {
              setConsent(checked === true)
            }}
          />
          {consent ? null : (
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Sem o aviso, a pessoa não consegue bater o ponto.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={!ready}
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const MembersTab = ({ canManage }: { canManage: boolean }): ReactNode => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<TimeclockMember | null | 'novo'>(null)
  const members = useQuery({
    queryKey: ['timeclock', 'members'],
    queryFn: async ({ signal }) => await fetchTimeclockMembers(signal),
  })
  const unlock = useMutation({
    mutationFn: unlockTimeclockMember,
    onSuccess: async () => {
      notify.success('Destravado.')
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
    },
    onError: (cause: unknown) => {
      notify.error(messageOf(cause, 'Não foi possível destravar.'))
    },
  })

  const missing = (row: TimeclockMember): string[] => {
    const member = row.member
    if (member === null) return ['sem código']
    return [
      ...(member.consentAt === null ? ['sem o aviso'] : []),
      ...(member.photoUrl === null ? ['sem foto'] : []),
    ]
  }

  return (
    <div className="flex flex-col gap-3">
      {canManage ? (
        <div>
          <Button
            variant="primary"
            onClick={() => {
              setEditing('novo')
            }}
          >
            Novo funcionário
          </Button>
        </div>
      ) : null}
      <QueryState
        isPending={members.isPending}
        isError={members.isError}
        error={members.error}
        onRetry={() => {
          void members.refetch()
        }}
        what="os funcionários"
        isEmpty={members.data?.members.length === 0}
        empty={
          <EmptyState
            title="Nenhum funcionário"
            description="Cadastre aqui, ou na Folha e gastos — é a mesma pessoa."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Funcionário</TableHeaderCell>
                <TableHeaderCell>Loja</TableHeaderCell>
                <TableHeaderCell>Onde bate</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? <TableHeaderCell>Ações</TableHeaderCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {(members.data?.members ?? []).map((row) => {
                const pending = missing(row)
                return (
                  <TableRow key={row.employeeId}>
                    <TableCell className="font-(--weight-medium)">
                      <span className="flex items-center gap-2">
                        {row.member?.photoUrl == null ? null : (
                          <img
                            src={row.member.photoUrl}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        )}
                        {row.name}
                      </span>
                    </TableCell>
                    <TableCell>{row.branchName}</TableCell>
                    <TableCell>
                      {row.member === null
                        ? '—'
                        : row.member.allowedModes
                            .map((mode) => MODE_LABELS[mode])
                            .join(' e ')}
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap gap-1">
                        {row.member?.lockedUntil == null ? null : (
                          <Badge tone="danger">
                            Travado até {hhmm(row.member.lockedUntil)}
                          </Badge>
                        )}
                        {pending.length === 0 ? (
                          <Badge tone="success">Pronto</Badge>
                        ) : (
                          pending.map((item) => (
                            <Badge key={item} tone="warning">
                              {item}
                            </Badge>
                          ))
                        )}
                      </span>
                    </TableCell>
                    {canManage ? (
                      <TableCell>
                        <span className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setEditing(row)
                            }}
                          >
                            {row.member === null ? 'Cadastrar' : 'Editar'}
                          </Button>
                          {row.member?.lockedUntil == null ? null : (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={
                                unlock.isPending &&
                                unlock.variables === row.employeeId
                              }
                              onClick={() => {
                                unlock.mutate(row.employeeId)
                              }}
                            >
                              Destravar
                            </Button>
                          )}
                        </span>
                      </TableCell>
                    ) : null}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
      {editing === null ? null : (
        <MemberDialog
          editing={editing === 'novo' ? null : editing}
          onClose={() => {
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aparelhos
// ---------------------------------------------------------------------------

const DeviceDialog = ({
  kind,
  onClose,
}: {
  kind: TimeclockMode
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
    enabled: kind === 'loja',
  })
  const members = useQuery({
    queryKey: ['timeclock', 'members'],
    queryFn: async ({ signal }) => await fetchTimeclockMembers(signal),
    enabled: kind === 'celular',
  })
  const [name, setName] = useState(kind === 'loja' ? 'Tablet do balcão' : '')
  const [target, setTarget] = useState('')
  const [link, setLink] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const phones = (members.data?.members ?? []).filter(
    (row) => row.member?.allowedModes.includes('celular') === true
  )

  const create = useMutation({
    mutationFn: async () =>
      await createTimeclockDevice(
        kind === 'loja'
          ? { kind, branchId: target, name }
          : { kind, employeeId: target, name }
      ),
    onSuccess: async (created) => {
      const ready = pairingLink(created)
      if (ready === null) {
        setError(
          'O aparelho foi criado, mas o servidor não tem o endereço público. Revogue, e gere o link pelo navegador.'
        )
      } else {
        setLink(ready)
      }
      await queryClient.invalidateQueries({
        queryKey: ['timeclock', 'devices'],
      })
    },
    onError: (cause: unknown) => {
      setError(messageOf(cause, 'Não foi possível criar o aparelho.'))
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={
          kind === 'loja' ? 'Aparelho da loja' : 'Celular de um funcionário'
        }
        description={
          kind === 'loja'
            ? 'O tablet ou computador mostra quem bate o ponto naquela loja.'
            : 'O celular bate o ponto só por aquela pessoa, com a localização.'
        }
      >
        {link === null ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              create.mutate()
            }}
          >
            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}
            {kind === 'loja' ? (
              <Field label="Loja" required>
                {(field) => (
                  <Select
                    {...field}
                    value={target}
                    onChange={(event) => {
                      setTarget(event.target.value)
                    }}
                  >
                    <option value="">Escolha a loja</option>
                    {(branches.data?.branches ?? [])
                      .filter((branch) => branch.status === 'active')
                      .map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            ) : (
              <Field
                label="Funcionário"
                required
                hint="Só aparece quem está liberado para bater no celular."
              >
                {(field) => (
                  <Select
                    {...field}
                    value={target}
                    onChange={(event) => {
                      const picked = phones.find(
                        (row) => row.employeeId === event.target.value
                      )
                      setTarget(event.target.value)
                      if (picked !== undefined)
                        setName(`Celular de ${picked.name}`)
                    }}
                  >
                    <option value="">Escolha a pessoa</option>
                    {phones.map((row) => (
                      <option key={row.employeeId} value={row.employeeId}>
                        {row.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label="Nome do aparelho" required>
              {(field) => (
                <Input
                  {...field}
                  value={name}
                  maxLength={80}
                  onChange={(event) => {
                    setName(event.target.value)
                  }}
                />
              )}
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={create.isPending}
                disabled={target === '' || name.trim() === ''}
              >
                Gerar o link
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <InlineAlert tone="warning">
              Este link aparece só agora. Abra no aparelho (ou leia o QR com a
              câmera dele) e salve na tela inicial.
            </InlineAlert>
            <PixQr text={link} size={220} alt="QR do link do aparelho" />
            <code className="w-full rounded-(--radius-sm) bg-(--surface-sunken) p-2 text-(length:--text-xs) break-all">
              {link}
            </code>
            <CopyButton value={link} label="Copiar o link" />
            <DialogFooter>
              <Button variant="primary" onClick={onClose}>
                Pronto
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const StoreOnMap = (): ReactNode => {
  const queryClient = useQueryClient()
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })
  const active = (branches.data?.branches ?? []).filter(
    (branch) => branch.status === 'active'
  )
  const [branchId, setBranchId] = useState('')
  const chosen = branchId === '' ? (active[0]?.id ?? '') : branchId
  const setting = useQuery({
    queryKey: ['timeclock', 'setting', chosen],
    queryFn: async ({ signal }) =>
      await fetchTimeclockBranchSetting(chosen, signal),
    enabled: chosen !== '',
  })
  const [draft, setDraft] = useState<{
    latitude: string
    longitude: string
    radius: string
  } | null>(null)
  const loaded = setting.data?.setting
  const values = draft ?? {
    latitude: loaded?.latitude ?? '',
    longitude: loaded?.longitude ?? '',
    radius: String(loaded?.radiusM ?? 150),
  }
  const [locating, setLocating] = useState(false)

  const save = useMutation({
    mutationFn: async () =>
      await saveTimeclockBranchSetting(chosen, {
        latitude: values.latitude === '' ? null : values.latitude,
        longitude: values.longitude === '' ? null : values.longitude,
        radiusM: Number(values.radius),
        version: loaded?.version ?? null,
      }),
    onSuccess: async () => {
      notify.success('Loja marcada no mapa.')
      setDraft(null)
      await queryClient.invalidateQueries({
        queryKey: ['timeclock', 'setting'],
      })
    },
    onError: async (cause: unknown) => {
      notify.error(messageOf(cause, 'Não foi possível salvar.'))
      // A versão velha: recarrega a loja e tira o rascunho, que era dela.
      if (isStale(cause)) {
        setDraft(null)
        await queryClient.invalidateQueries({
          queryKey: ['timeclock', 'setting'],
        })
      }
    },
  })

  const here = () => {
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false)
        setDraft({
          ...values,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        })
      },
      () => {
        setLocating(false)
        notify.error('O navegador não deu a localização.')
      },
      { enableHighAccuracy: true, timeout: 15_000 }
    )
  }

  return (
    <Section
      title="A loja no mapa"
      description="Para a batida pelo celular: longe da loja ela entra marcada “fora da loja”. Sem a loja no mapa, a batida só guarda onde foi."
    >
      {branches.isError || setting.isError ? (
        <InlineAlert tone="danger">
          <span className="flex flex-wrap items-center gap-2">
            Não foi possível carregar as lojas.
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void branches.refetch()
                if (chosen !== '') void setting.refetch()
              }}
            >
              Tentar de novo
            </Button>
          </span>
        </InlineAlert>
      ) : branches.isSuccess && active.length === 0 ? (
        <InlineAlert tone="info">Nenhuma loja ativa para marcar.</InlineAlert>
      ) : null}
      <form
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <Field label="Loja">
          {(field) => (
            <Select
              {...field}
              value={chosen}
              onChange={(event) => {
                setBranchId(event.target.value)
                setDraft(null)
              }}
            >
              {active.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Latitude">
          {(field) => (
            <Input
              {...field}
              value={values.latitude}
              inputMode="decimal"
              onChange={(event) => {
                setDraft({ ...values, latitude: event.target.value.trim() })
              }}
            />
          )}
        </Field>
        <Field label="Longitude">
          {(field) => (
            <Input
              {...field}
              value={values.longitude}
              inputMode="decimal"
              onChange={(event) => {
                setDraft({ ...values, longitude: event.target.value.trim() })
              }}
            />
          )}
        </Field>
        <Field label="Raio (metros)" hint="De 30 a 5.000.">
          {(field) => (
            <Input
              {...field}
              value={values.radius}
              inputMode="numeric"
              onChange={(event) => {
                setDraft({
                  ...values,
                  radius: event.target.value.replace(/\D/gu, ''),
                })
              }}
            />
          )}
        </Field>
        <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-4">
          <Button
            type="button"
            variant="secondary"
            loading={locating}
            onClick={here}
          >
            <MapPin aria-hidden="true" className="h-4 w-4" />
            Usar a localização deste aparelho
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={chosen === '' || setting.isPending}
          >
            Salvar
          </Button>
        </div>
      </form>
    </Section>
  )
}

const DevicesTab = (): ReactNode => {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState<TimeclockMode | null>(null)
  const devices = useQuery({
    queryKey: ['timeclock', 'devices'],
    queryFn: async ({ signal }) => await fetchTimeclockDevices(signal),
  })
  const [revoking, setRevoking] = useState<TimeclockDevice | null>(null)
  const revoke = useMutation({
    mutationFn: async (input: {
      readonly device: TimeclockDevice
      readonly reason: string
    }) =>
      await revokeTimeclockDevice(
        input.device.id,
        input.reason.trim() === '' ? null : input.reason.trim()
      ),
    onSuccess: async () => {
      setRevoking(null)
      notify.success('Aparelho revogado. Ele para de bater na hora.')
      await queryClient.invalidateQueries({
        queryKey: ['timeclock', 'devices'],
      })
    },
    onError: (cause: unknown) => {
      notify.error(messageOf(cause, 'Não foi possível revogar.'))
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          onClick={() => {
            setCreating('loja')
          }}
        >
          <Tablet aria-hidden="true" className="h-4 w-4" />
          Parear aparelho da loja
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setCreating('celular')
          }}
        >
          <Smartphone aria-hidden="true" className="h-4 w-4" />
          Parear celular de um funcionário
        </Button>
      </div>
      <QueryState
        isPending={devices.isPending}
        isError={devices.isError}
        error={devices.error}
        onRetry={() => {
          void devices.refetch()
        }}
        what="os aparelhos"
        isEmpty={devices.data?.devices.length === 0}
        empty={
          <EmptyState
            title="Nenhum aparelho"
            description="Gere o link de um aparelho e abra nele: o tablet da loja ou o celular de alguém."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Aparelho</TableHeaderCell>
                <TableHeaderCell>Bate por</TableHeaderCell>
                <TableHeaderCell>Visto por último</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell>Ações</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(devices.data?.devices ?? []).map((device) => (
                <TableRow key={device.id}>
                  <TableCell className="font-(--weight-medium)">
                    {device.name}
                  </TableCell>
                  <TableCell>
                    {device.kind === 'celular'
                      ? (device.employeeName ?? '—')
                      : `Loja ${device.branchName}`}
                  </TableCell>
                  <TableCell>{when(device.lastSeenAt)}</TableCell>
                  <TableCell>
                    {device.revokedAt === null ? (
                      <Badge tone="success">Liberado</Badge>
                    ) : (
                      <Badge tone="neutral">Revogado</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {device.revokedAt === null ? (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setRevoking(device)
                        }}
                      >
                        Revogar
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>
      <StoreOnMap />
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null)
        }}
        title="Revogar o aparelho"
        description={`"${revoking?.name ?? ''}" para de bater o ponto na hora. Para voltar, é preciso gerar um link novo e abrir nele.`}
        confirmLabel="Revogar"
        reason={{ label: 'Motivo', placeholder: 'Ex.: tablet trocado' }}
        loading={revoke.isPending}
        onConfirm={(reason) => {
          if (revoking !== null) {
            revoke.mutate({ device: revoking, reason: reason ?? '' })
          }
        }}
      />
      {creating === null ? null : (
        <DeviceDialog
          kind={creating}
          onClose={() => {
            setCreating(null)
          }}
        />
      )}
    </div>
  )
}

export const PontoPage = (): ReactNode => {
  const { allows } = useAuth()
  const canManage = allows('timeclock.manage')

  return (
    <Page>
      <PageHeader
        title="Ponto"
        description="Controle interno: quem está na loja, as batidas com foto, o espelho do mês e a escala."
      />
      <Tabs defaultValue="hoje">
        <TabsList aria-label="Ponto">
          <TabsTrigger value="hoje">Hoje</TabsTrigger>
          <TabsTrigger value="espelho">Espelho do mês</TabsTrigger>
          <TabsTrigger value="escala">Escala</TabsTrigger>
          <TabsTrigger value="funcionarios">Funcionários</TabsTrigger>
          {canManage ? (
            <TabsTrigger value="aparelhos">Aparelhos</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="hoje">
          <TodayTab />
        </TabsContent>
        <TabsContent value="espelho">
          <PontoEspelho canManage={canManage} />
        </TabsContent>
        <TabsContent value="escala">
          <PontoEscala canManage={canManage} />
        </TabsContent>
        <TabsContent value="funcionarios">
          <MembersTab canManage={canManage} />
        </TabsContent>
        {canManage ? (
          <TabsContent value="aparelhos">
            <DevicesTab />
          </TabsContent>
        ) : null}
      </Tabs>
    </Page>
  )
}
